import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import GranjaMovimientosLinkButton from '../../components/granja/GranjaMovimientosLinkButton';
import { useFechaOperativa } from '../../contexts/FechaOperativaContext';
import { useSucursalesUsuarioContext } from '../../contexts/SucursalesUsuarioContext';
import granjaService, { GranjaConfig, resolveNroSucOperativo } from '../../services/granjaService';
import productoDepositoService from '../../services/productoDepositoService';
import { getApiErrorMessage } from '../../utils/apiError';
import { toLocalDateStringYMD } from '../../utils/formatters';
import {
  diferenciaBalance,
  HUEVOS_POR_MAPLE,
  maplesYHuevosAHuevos,
} from '../../utils/granjaCantidades';

type CantidadPar = { maples: string; huevos: string };

const emptyPar = (): CantidadPar => ({ maples: '', huevos: '' });

const soloEnteros = (value: string) => value.replace(/\D/g, '');

const parAHuevos = (par: CantidadPar): number =>
  maplesYHuevosAHuevos(Number(par.maples) || 0, Number(par.huevos) || 0);

/** Artículos bruto de lotes activos, únicos (misma base FIFO del API). */
const articulosBrutoActivos = (config: GranjaConfig | null): string[] => {
  if (!config) return [];
  const seen = new Set<string>();
  const codes: string[] = [];
  const lotes = [...config.lotes]
    .filter((l) => l.activo && l.articuloHuevoBruto?.trim())
    .sort((a, b) => {
      const fa = a.fechaIngreso ? new Date(a.fechaIngreso).getTime() : Number.MAX_SAFE_INTEGER;
      const fb = b.fechaIngreso ? new Date(b.fechaIngreso).getTime() : Number.MAX_SAFE_INTEGER;
      if (fa !== fb) return fa - fb;
      return a.codigo.localeCompare(b.codigo, undefined, { sensitivity: 'base' });
    });
  for (const lote of lotes) {
    const cod = lote.articuloHuevoBruto.trim().toUpperCase();
    if (seen.has(cod)) continue;
    seen.add(cod);
    codes.push(lote.articuloHuevoBruto.trim());
  }
  return codes;
};

const GranjaClasificacion: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { preferidaNro } = useSucursalesUsuarioContext();
  const { fechaYmd } = useFechaOperativa();
  const diaOperativo = fechaYmd ?? toLocalDateStringYMD();
  const [config, setConfig] = useState<GranjaConfig | null>(null);
  const [saldoHuevos, setSaldoHuevos] = useState(0);
  const [cantidadBrutaHuevos, setCantidadBrutaHuevos] = useState('');
  const [cantidades, setCantidades] = useState<Record<string, CantidadPar>>({});
  const [cascado, setCascado] = useState<CantidadPar>(emptyPar());
  const [tirado, setTirado] = useState<CantidadPar>(emptyPar());
  const [saving, setSaving] = useState(false);
  const [loadingSaldo, setLoadingSaldo] = useState(false);

  const cargarSaldo = useCallback(async (cfg: GranjaConfig | null) => {
    const arts = articulosBrutoActivos(cfg);
    if (!cfg?.depositoGranja || arts.length === 0) {
      setSaldoHuevos(0);
      setCantidadBrutaHuevos('');
      return;
    }
    try {
      setLoadingSaldo(true);
      const saldos = await Promise.all(
        arts.map(async (codigo) => {
          const res = await productoDepositoService.getDepositos(codigo);
          const dep = res.depositos.find((d) => d.nroUbicacion === cfg.depositoGranja);
          return Number(dep?.cantidad ?? 0);
        }),
      );
      const total = Math.round(saldos.reduce((acc, n) => acc + n, 0));
      setSaldoHuevos(total);
      setCantidadBrutaHuevos(total > 0 ? String(total) : '');
    } catch {
      setSaldoHuevos(0);
      setCantidadBrutaHuevos('');
    } finally {
      setLoadingSaldo(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const cfg = await granjaService.getConfig();
        setConfig(cfg);
      } catch {
        enqueueSnackbar('Error al cargar configuración', { variant: 'error' });
      }
    })();
  }, [enqueueSnackbar]);

  useEffect(() => {
    void cargarSaldo(config);
  }, [config, cargarSaldo]);

  const brutoHuevos = Number(cantidadBrutaHuevos) || 0;
  const vendibleHuevos = (config?.articulosClasificacion ?? []).reduce((acc, a) => {
    return acc + parAHuevos(cantidades[a.codigoArticulo] ?? emptyPar());
  }, 0);
  const cantCascadoHuevos = parAHuevos(cascado);
  const cantTiradoHuevos = parAHuevos(tirado);
  const clasificadoHuevos = vendibleHuevos + cantCascadoHuevos + cantTiradoHuevos;
  const pendienteHuevos = brutoHuevos - clasificadoHuevos;
  const excedeBruto = clasificadoHuevos > brutoHuevos && brutoHuevos > 0;
  const balanceExacto = clasificadoHuevos === brutoHuevos && brutoHuevos > 0;
  const diffHuevos = Math.round(
    diferenciaBalance({
      bruto: brutoHuevos,
      vendible: vendibleHuevos,
      cascado: cantCascadoHuevos,
      tirado: cantTiradoHuevos,
    }),
  );

  const updateCantidad = (codigo: string, field: keyof CantidadPar, value: string) => {
    const cleaned = soloEnteros(value);
    setCantidades((prev) => ({
      ...prev,
      [codigo]: { ...(prev[codigo] ?? emptyPar()), [field]: cleaned },
    }));
  };

  const handleConfirmar = async () => {
    if (!config) return;
    if (excedeBruto) {
      enqueueSnackbar('No se puede clasificar más que la cantidad bruta', { variant: 'error' });
      return;
    }
    if (clasificadoHuevos <= 0) {
      enqueueSnackbar('Indicá al menos una cantidad clasificada, cascado o tirado', {
        variant: 'warning',
      });
      return;
    }
    try {
      setSaving(true);
      const nroSuc = await resolveNroSucOperativo(preferidaNro);
      if (!nroSuc) {
        enqueueSnackbar('No hay sucursal operativa asignada al usuario', { variant: 'warning' });
        return;
      }
      const result = await granjaService.clasificar({
        cantidadBruta: brutoHuevos,
        vendibles: (config.articulosClasificacion ?? [])
          .map((a) => ({
            codigoArticulo: a.codigoArticulo,
            cantidad: parAHuevos(cantidades[a.codigoArticulo] ?? emptyPar()),
            calibre: a.calibre,
            color: a.color,
          }))
          .filter((v) => v.cantidad > 0),
        cantidadCascado: cantCascadoHuevos,
        codigoArticuloCascado: config.articuloCascado,
        cantidadTirado: cantTiradoHuevos,
        nroUbicacion: config.depositoGranja,
        nroSuc,
        fecha: diaOperativo,
      });
      enqueueSnackbar('Clasificación registrada', { variant: 'success' });
      if (result.advertencia) {
        enqueueSnackbar(result.advertencia, { variant: 'warning' });
      }
      setCantidades({});
      setCascado(emptyPar());
      setTirado(emptyPar());
      await cargarSaldo(config);
    } catch (err: unknown) {
      enqueueSnackbar(getApiErrorMessage(err, 'Error al clasificar'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const saldoMaples = saldoHuevos / HUEVOS_POR_MAPLE;

  const renderParFields = (
    label: string,
    par: CantidadPar,
    onChange: (field: keyof CantidadPar, value: string) => void,
    key?: string,
  ) => (
    <Box key={key ?? label}>
      <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 600 }}>
        {label}
      </Typography>
      <Stack direction="row" spacing={1.5}>
        <TextField
          label="Maples"
          value={par.maples}
          onChange={(e) => onChange('maples', e.target.value)}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          fullWidth
        />
        <TextField
          label="Huevos"
          value={par.huevos}
          onChange={(e) => onChange('huevos', e.target.value)}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          fullWidth
        />
      </Stack>
    </Box>
  );

  const alertSeverity = excedeBruto
    ? 'error'
    : balanceExacto
      ? 'success'
      : clasificadoHuevos > 0
        ? 'warning'
        : 'info';

  const alertText = excedeBruto
    ? `No se puede clasificar de más: exceso ${Math.abs(diffHuevos).toLocaleString('es-AR')} huevos.`
    : balanceExacto
      ? 'Balance correcto'
      : clasificadoHuevos > 0
        ? `Advertencia: diferencia ${diffHuevos.toLocaleString('es-AR')} huevos (bruto − clasificado). Podés confirmar: el restante queda en stock.`
        : 'Ingresá calibres, cascado y/o tirado. No puede superar la cantidad bruta.';

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ sm: 'center' }}
              justifyContent="space-between"
            >
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Clasificación
              </Typography>
              <GranjaMovimientosLinkButton tipo="CLASIFICACION" />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Transforma el huevo bruto en stock comercializable. El bruto se toma por FIFO de los
              lotes activos. No se puede clasificar más que la bruta; si es menos, el resto queda en
              stock.
            </Typography>

            <Alert
              severity={pendienteHuevos === 0 && brutoHuevos > 0 ? 'success' : 'info'}
              sx={{
                '& .MuiAlert-message': { width: '100%' },
                py: 1.5,
              }}
            >
              <Typography variant="overline" sx={{ display: 'block', lineHeight: 1.2, opacity: 0.8 }}>
                Cantidad pendiente de clasificar
              </Typography>
              <Typography
                variant="h3"
                component="p"
                sx={{
                  fontWeight: 700,
                  color: pendienteHuevos === 0 && brutoHuevos > 0 ? 'success.dark' : 'primary.main',
                  my: 0.5,
                }}
              >
                {pendienteHuevos.toLocaleString('es-AR')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                huevos
              </Typography>
            </Alert>

            <TextField
              label="Cantidad bruta a clasificar (huevos)"
              value={cantidadBrutaHuevos}
              onChange={(e) => setCantidadBrutaHuevos(soloEnteros(e.target.value))}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              helperText={
                loadingSaldo
                  ? 'Cargando saldo…'
                  : `Saldo en Granja (FIFO): ${saldoHuevos.toLocaleString('es-AR')} huevos (${saldoMaples.toFixed(2)} maples)`
              }
              fullWidth
            />

            {(config?.articulosClasificacion ?? []).map((a) =>
              renderParFields(
                `${a.calibre} ${a.color} (${a.codigoArticulo})`,
                cantidades[a.codigoArticulo] ?? emptyPar(),
                (field, value) => updateCantidad(a.codigoArticulo, field, value),
                a.codigoArticulo,
              ),
            )}

            {renderParFields(
              `Cascado / Roto / Sucio (${config?.articuloCascado || '—'})`,
              cascado,
              (field, value) =>
                setCascado((prev) => ({ ...prev, [field]: soloEnteros(value) })),
            )}
            {renderParFields('Tirado (merma)', tirado, (field, value) =>
              setTirado((prev) => ({ ...prev, [field]: soloEnteros(value) })),
            )}

            <Alert severity={alertSeverity}>{alertText}</Alert>

            <Button
              variant="contained"
              color="primary"
              size="large"
              disabled={saving || brutoHuevos <= 0 || clasificadoHuevos <= 0 || excedeBruto}
              onClick={handleConfirmar}
              sx={{ minHeight: 56, fontWeight: 700 }}
            >
              {saving ? 'Procesando…' : 'Confirmar clasificación'}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default GranjaClasificacion;
